import { useNavigate } from "@solidjs/router"
import { createMemo, createSignal } from "solid-js"
import { ulid } from "ulid"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Form } from "@/designSystem/components/form"
import { Text } from "@/designSystem/components/text"
import { TextField } from "@/designSystem/components/textField"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { FileUpload } from "@/designSystem/patterns/file-upload"
import { Navigation } from "@/designSystem/patterns/navigation"
import { Section } from "@/designSystem/patterns/section"
import { responsivePaddingClass } from "@/designSystem/utility/responsivePadding.css"
import {
  generateOpmlFeeds,
  type OpmlFeed,
  parseOpmlFeeds,
} from "@/lib/opml-feeds"
import { createPublishersRouter } from "@/routes/publishers-router"

export function PublisherSettings() {
  return <Settings />
}

type Props = {
  topLevel?: boolean
}
export function Settings(props: Props) {
  const buttons = createMemo(() => {
    if (props.topLevel) return []

    return [
      {
        label: "feeds",
        href: "/feeds/subscriptions",
      } as const,
    ]
  })

  return (
    <MainLayout
      header={<Navigation hue="indigo" buttons={buttons()} title="subscribe" />}
    >
      <Box gap="32">
        <SubscribeToPublicationForm />
        <Box class={responsivePaddingClass}>
          <Section
            title="import/export OPML feeds"
            hue="indigo"
            iconName="rss"
            description="import feed subscriptions from RSS readers or export your subscriptions to OPML files compatible with any feed reader."
          >
            <OpmlFeedsForm />
          </Section>
        </Box>
      </Box>
    </MainLayout>
  )
}

function SubscribeToPublicationForm() {
  const { replicache, db } = useAppContext()
  const navigate = useNavigate()
  const [publisherUrl, setPublisherUrl] = createSignal<string | undefined>()
  const dirty = createMemo(() => !!publisherUrl()?.length)
  const { paths } = createPublishersRouter()

  async function onSubmit() {
    const url = publisherUrl()
    if (!url) return

    const id = ulid()
    await replicache.mutate.subscribePublisher({
      publisher_id: id,
      subscription_id: ulid(),
      url,
    })
    const newPublisher = db.publisher.get(id)
    navigate(paths().publisher(newPublisher!).url)
  }

  return (
    <Box class={responsivePaddingClass}>
      <Form
        title="subscribe to feeds"
        hue="indigo"
        iconName="newspaper"
        description="Copy and paste the address of the feed you want to add. You can find it on top of your browser."
        onSubmit={onSubmit}
      >
        <TextField value={publisherUrl()} onChange={setPublisherUrl}>
          <TextField.Label>feed address (url)</TextField.Label>
          <TextField.Input type="text" placeholder="https://" />
        </TextField>
        <Form.Save hue="indigo" iconName="newspaper" disabled={!dirty()} />
      </Form>
    </Box>
  )
}

function OpmlFeedsForm() {
  const { replicache, db } = useAppContext()
  const [importing, setImporting] = createSignal(false)
  const [exporting, setExporting] = createSignal(false)

  async function handleImport(file: File | null) {
    if (!file) return

    setImporting(true)

    try {
      const text = await file.text()
      const feeds = parseOpmlFeeds(text)

      const subscriptions = feeds.flatMap(feed => {
        const existingPublisher = db.publisher.findBy({ url: feed.url })

        if (existingPublisher) {
          const publisher_id = existingPublisher.id
          const existingSubscription = db.subscription.findBy({ publisher_id })
          if (existingSubscription) return []
        }

        return {
          publisher_id: ulid(),
          subscription_id: ulid(),
          url: feed.url,
        }
      })

      await replicache.mutate.importSubscriptions({
        subscriptions,
        skipped: feeds.length - subscriptions.length,
      })
    } catch (error) {
      console.error("Import error:", error)
      replicache.mutate.initLog({
        id: ulid(),
        title: "Error importing feeds. Please check the OPML file format.",
        status: "Failed",
        created_at: new Date().toISOString(),
      })
    } finally {
      setImporting(false)
    }
  }

  function handleExport() {
    setExporting(true)

    try {
      const subscriptions = db.subscription.all()
      const publishers = db.publisher.all()

      const opmlFeeds: OpmlFeed[] = subscriptions
        .map(subscription => {
          const publisher = publishers.find(
            p => p.id === subscription.publisher_id,
          )
          if (!publisher) return null

          return {
            url: publisher.url,
            title: publisher.dc_title || publisher.url,
            description: publisher.dc_description || undefined,
            htmlUrl: publisher.url,
          }
        })
        .filter(Boolean) as OpmlFeed[]

      if (opmlFeeds.length === 0) {
        setExporting(false)
        replicache.mutate.initLog({
          id: ulid(),
          title: "No feed subscriptions to export.",
          status: "Failed",
          created_at: new Date().toISOString(),
        })
        return
      }

      const xml = generateOpmlFeeds(opmlFeeds, "Fika Feed Subscriptions")
      const blob = new Blob([xml], { type: "text/xml;charset=utf-8" })
      const url = URL.createObjectURL(blob)

      const a = document.createElement("a")
      a.href = url
      a.download = `fika-feeds-${new Date().toISOString().split("T")[0]}.opml`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      replicache.mutate.initLog({
        id: ulid(),
        title: `Exported ${opmlFeeds.length} feed subscriptions.`,
        status: "Success",
        created_at: new Date().toISOString(),
      })
    } catch (error) {
      console.error("Export error:", error)
      replicache.mutate.initLog({
        id: ulid(),
        title: "Error exporting feed subscriptions.",
        status: "Failed",
        created_at: new Date().toISOString(),
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <Box gap="24" fill>
      <Well>
        <Box gap="24" fill>
          <Box>
            <Text fontSize="m" fontWeight="500">
              Import OPML Feeds
            </Text>
            <Text fontSize="s" color="sand700">
              Select an OPML file (usually exported from RSS readers like
              Feedly, Thunderbird, Outlook, etc.)
            </Text>
          </Box>
          <FileUpload
            onUpload={handleImport}
            text={importing() ? "importing..." : "choose file"}
            hue="indigo"
            accept=".opml,.xml"
            iconName="rss"
            disabled={importing()}
          />
        </Box>
      </Well>
      <Well>
        <Box gap="24" fill>
          <Box>
            <Text fontSize="m" fontWeight="500">
              Export OPML Feeds
            </Text>
            <Text fontSize="s" color="sand700">
              Download all your feed subscriptions as an OPML file that can be
              imported into any RSS reader or feed aggregator.
            </Text>
          </Box>
          <Box alignItems="flex-end">
            <Button
              hue="indigo"
              iconName="download-cloud"
              onClick={handleExport}
              disabled={exporting()}
            >
              {exporting() ? "exporting..." : "download file"}
            </Button>
          </Box>
        </Box>
      </Well>
    </Box>
  )
}
